import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Pencil } from 'lucide-react';
import Lottie from 'lottie-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  EmailAuthProvider,
  multiFactor,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator,
  type MultiFactorInfo,
  type TotpSecret,
  updatePassword,
  updateProfile,
} from 'firebase/auth';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  FullWidthYellowPanel,
  LongRedPanel,
  SmallGrayPanel,
  SmallGreenPanel,
  backendPalette,
} from '../../components/backend/BackendCardSystem';
import { useAuth } from '../../context/AuthProvider';
import { auth, storage } from '../../lib/firebase';
import { getPasswordPolicyError } from '../../lib/passwordPolicy';
import { dispatchUserProfileUpdated } from '../../lib/userProfileEvents';
import { formatUsPhone, isValidUsPhone } from '../../lib/usPhone';
import {
  type ClientOrgBusinessProfile,
  fetchClientOrgBusinessProfile,
  updateClientOrgBusinessProfile,
} from '../../lib/clientOrganization';
import {
  type DiscordLinkStatus,
  fetchDiscordLinkStatus,
  requestDiscordAuthorizeUrl,
  unlinkDiscordAccount,
} from '../../api/discord';
import discordAnimation from '../../assets/discord.json';

type SubSnapshot = {
  status: string | null;
  current_period_end: number | string | null;
  cancel_at: number | string | null;
  trial_end?: number | string | null;
  nextBillingAt?: number | string | null;
  planId: string | null;
  planDisplayName?: string | null;
  metadata?: Record<string, unknown> | null;
  items?: {
    data?: Array<{
      price?: {
        nickname?: string | null;
        product?: { name?: string | null } | string | null;
      } | null;
    }>;
  } | null;
};

type UserMeResponse = {
  uid?: string;
  email?: string | null;
  role?: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  photoUrl?: string | null;
  photoStoragePath?: string | null;
  bio?: string | null;
  userType?: 'business' | 'individual' | null;
  contactPhone?: string | null;
  discordHandle?: string | null;
  portfolioWebsite?: string | null;
  businessWebsite?: string | null;
  businessPhone?: string | null;
  team?: string | null;
  title?: string | null;
  supervisorUid?: string | null;
  supervisorName?: string | null;
  agentTitle?: string | null;
  subscriptionStatus?: string | null;
  planId?: string | null;
  createdAt?: string | number | { seconds?: number; _seconds?: number };
};

type EditableProfileForm = {
  firstName: string;
  lastName: string;
  photoURL: string;
  photoStoragePath: string;
  userType: '' | 'business' | 'individual';
  contactPhone: string;
  portfolioWebsite: string;
  email: string;
  businessWebsite: string;
  team: string;
  title: string;
  supervisorName: string;
  bio: string;
};

type OrgBusinessForm = {
  businessName: string;
  logoURL: string;
  logoStoragePath: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  address: string;
};

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const PLAN_NAME: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  growth: 'Growth',
  marketing: 'Managed Marketing',
  'digital-services': 'Assets & Design',
  'ecommerce-digital-services': 'Platform Development',
};

const MAX_AVATAR_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_AVATAR_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);
const MAX_ORG_LOGO_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_ORG_LOGO_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

function extensionFromMimeType(mimeType: string): 'jpg' | 'png' | 'webp' {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  return 'jpg';
}

function toDate(value: UserMeResponse['createdAt']): Date | null {
  if (!value) return null;
  if (typeof value === 'number') return new Date(value > 1e12 ? value : value * 1000);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const seconds = value.seconds ?? value._seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  return null;
}

function formatMonthYear(date: Date | null): string {
  if (!date) return '--';
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function resolvePhotoURL(data: UserMeResponse): string | null {
  // Regression guard: backend user payloads are sourced from Prisma `photoUrl`
  // but normalized to `photoURL` in most responses. Keep this dual-read so
  // avatar rendering does not silently fall back to initials.
  const value = data.photoURL ?? data.photoUrl ?? null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Heading = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: clamp(3.25rem, 2.2vw, 1.6rem);
  color: #e0a15e;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 1.05rem;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MotionLongRedPanel = motion(LongRedPanel);
const SideCard = styled(MotionLongRedPanel)`
  padding: ${({ theme }) => theme.spacing(4)};
  text-align: center;
`;

const Avatar = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 999px;
  margin: 0 auto ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 2.9rem;
  font-weight: 800;
  overflow: visible;
  isolation: isolate;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
  display: block;
`;

const AvatarEditButton = styled.button`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.92);
  background: rgba(26, 26, 26, 0.9);
  color: #ffffff;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 8px 14px rgba(0, 0, 0, 0.38);
  z-index: 3;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    color: #ffffff;
    stroke: #ffffff;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Name = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(0.5)};
  color: ${backendPalette.black};
  font-size: 1.45rem;
`;

const Email = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  color: rgba(26, 26, 26, 0.78);
`;

const RoleBadge = styled.span<{ $role: string }>`
  display: inline-block;
  border-radius: 999px;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: capitalize;
  background: ${({ $role }) =>
    $role === 'admin'
      ? '#dbeafe'
      : $role === 'agent'
      ? '#e0e7ff'
      : $role === 'client'
      ? '#d1fae5'
      : '#fef3c7'};
  color: ${({ $role }) =>
    $role === 'admin'
      ? '#1e40af'
      : $role === 'agent'
      ? '#4338ca'
      : $role === 'client'
      ? '#065f46'
      : '#92400e'};
`;

const InfoSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid rgba(26, 26, 26, 0.26);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)} 0;
  font-size: 0.9rem;
`;

const InfoLabel = styled.span`
  color: rgba(26, 26, 26, 0.76);
`;

const InfoValue = styled.span`
  color: ${backendPalette.black};
  font-weight: 800;
  text-align: right;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const MotionYellowPanel = motion(FullWidthYellowPanel);
const ContentCard = styled(MotionYellowPanel)`
  overflow: hidden;
  padding: 0;
`;

const CardHeader = styled.div`
  border-bottom: 1px solid rgba(26, 26, 26, 0.3);
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const CardTitle = styled.h3`
  margin: 0;
  color: ${backendPalette.black};
  font-size: 1.2rem;
  font-weight: 900;
`;

const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Label = styled.span`
  color: rgba(26, 26, 26, 0.82);
  font-weight: 800;
  font-size: 0.88rem;
`;

const Input = styled.input<{ $editing: boolean; $empty?: boolean }>`
  padding: ${({ theme }) => theme.spacing(1.5)};
  border: ${({ $editing }) =>
    $editing ? '1px solid rgba(26, 26, 26, 0.34)' : '1px solid transparent'};
  border-radius: ${({ theme }) => theme.spacing(1)};
  font-size: 0.95rem;
  background: transparent;
  color: ${({ $editing, $empty }) =>
    !$editing && $empty ? backendPalette.red : backendPalette.black};
  transition: 0.2s;
  cursor: ${({ $editing }) => ($editing ? 'text' : 'default')};

  &::placeholder {
    color: rgba(26, 26, 26, 0.58);
  }

  &:focus {
    outline: none;
    border: ${({ $editing }) =>
      $editing ? '2px solid rgba(26, 26, 26, 1)' : '1px solid transparent'};
    box-shadow: none;
  }
`;

const Textarea = styled.textarea<{ $editing: boolean; $empty?: boolean }>`
  padding: ${({ theme }) => theme.spacing(1.5)};
  border: ${({ $editing }) =>
    $editing ? '1px solid rgba(26, 26, 26, 0.34)' : '1px solid transparent'};
  border-radius: ${({ theme }) => theme.spacing(1)};
  font-size: 0.95rem;
  background: transparent;
  color: ${({ $editing, $empty }) =>
    !$editing && $empty ? backendPalette.red : backendPalette.black};
  min-height: 100px;
  resize: vertical;
  transition: 0.2s;
  cursor: ${({ $editing }) => ($editing ? 'text' : 'default')};

  &::placeholder {
    color: rgba(26, 26, 26, 0.58);
  }

  &:focus {
    outline: none;
    border: ${({ $editing }) =>
      $editing ? '2px solid rgba(26, 26, 26, 1)' : '1px solid transparent'};
    box-shadow: none;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ $variant }) => ($variant === 'danger' ? '#a64c44' : 'rgba(26, 26, 26, 0.34)')};
  border-radius: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(1.4)} ${theme.spacing(3)}`};
  font-size: 0.92rem;
  font-weight: 900;
  cursor: pointer;
  text-decoration: none;
  transition: 0.2s;
  background: ${({ $variant }) =>
    $variant === 'secondary'
      ? 'rgba(255, 255, 255, 0.32)'
      : $variant === 'danger'
      ? '#a64c44'
      : backendPalette.black};
  color: ${({ $variant }) =>
    $variant === 'secondary'
      ? backendPalette.black
      : $variant === 'danger'
      ? '#ffffff'
      : backendPalette.yellow};

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Notice = styled(motion.div)<{ $type: 'success' | 'error' }>`
  border-radius: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(2)};
  font-size: 0.9rem;
  font-weight: 700;
  color: ${({ $type }) => ($type === 'success' ? '#065f46' : '#991b1b')};
  background: ${({ $type }) => ($type === 'success' ? '#d1fae5' : '#fee2e2')};
`;

const OrgProfileIntro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  color: rgba(26, 26, 26, 0.72);
  font-size: 0.9rem;
`;

const OrgProfileRow = styled.div`
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const OrgLogoPane = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  justify-items: start;
  align-self: center;

  @media (max-width: 900px) {
    align-self: start;
  }
`;

const OrgLogoFrame = styled.div`
  position: relative;
  width: 132px;
  height: 132px;
`;

const OrgLogo = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 14px;
  border: 1px dashed rgba(26, 26, 26, 0.4);
  display: grid;
  place-items: center;
  overflow: hidden;
  color: rgba(26, 26, 26, 0.86);
  font-weight: 900;
  background: rgba(255, 255, 255, 0.2);
  font-size: 1.55rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const OrgLogoEditButton = styled.button`
  position: absolute;
  right: 0.45rem;
  bottom: 0.45rem;
  padding: 0.28rem 0.52rem;
  border-radius: 8px;
  border: 1px solid rgba(26, 26, 26, 0.24);
  background: rgba(26, 26, 26, 0.9);
  color: #fff;
  font-weight: 800;
  font-size: 0.72rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const OrgProfileFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const OrgSetupPanel = styled.div`
  border: 1px dashed rgba(26, 26, 26, 0.35);
  border-radius: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: rgba(255, 255, 255, 0.18);
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const OrgSetupTitle = styled.h4`
  margin: 0;
  color: ${backendPalette.black};
  font-size: 1rem;
  font-weight: 900;
`;

const OrgSetupBody = styled.p`
  margin: 0;
  color: rgba(26, 26, 26, 0.82);
  font-size: 0.92rem;
  line-height: 1.45;
`;

const SettingsPanelRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsGreenPanel = styled(SmallGreenPanel)`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.1)};
  align-content: start;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const SettingsGrayPanel = styled(SmallGrayPanel)`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.1)};
  align-content: start;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const SettingsPanelTitle = styled.h3`
  margin: 0;
  color: ${backendPalette.black};
  font-size: 1.1rem;
  font-weight: 900;
`;

const SettingsField = styled.label`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.6)};
`;

const SettingsFieldLabel = styled.span`
  color: rgba(26, 26, 26, 0.8);
  font-size: 0.85rem;
  font-weight: 800;
`;

const SettingsValue = styled.div`
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(26, 26, 26, 0.22);
  background: rgba(255, 255, 255, 0.28);
  color: ${backendPalette.black};
  font-weight: 800;
  font-size: 0.92rem;
  padding: 10px 12px;
  display: flex;
  align-items: center;
`;

const SettingsInlineRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const SettingsInlineValue = styled(SettingsValue)`
  min-width: 0;
`;

const SettingsPanelBody = styled.p`
  margin: 0;
  color: rgba(26, 26, 26, 0.86);
  font-size: 0.92rem;
  line-height: 1.45;

  strong {
    color: ${backendPalette.red};
    font-weight: 900;
  }
`;

const SettingsActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const CommunityActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1.1)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: 14px;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const DiscordLottieBadge = styled.div`
  width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const CommunityActionButton = styled(Button)`
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
`;

const SecurityActionButton = styled.button`
  border-radius: 10px;
  min-height: 40px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: ${backendPalette.black};
  color: ${backendPalette.red};
  font-size: 0.84rem;
  font-weight: 900;
  padding: 0 14px;
  cursor: pointer;
`;

const SecurityStatus = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#7f1d1d' : 'rgba(26, 26, 26, 0.86)')};
  font-size: 0.82rem;
  font-weight: 800;
`;

const MfaBlock = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
  padding-top: ${({ theme }) => theme.spacing(0.8)};
  border-top: 1px dashed rgba(26, 26, 26, 0.26);
`;

const MfaTitle = styled.div`
  color: ${backendPalette.black};
  font-size: 0.92rem;
  font-weight: 900;
`;

const MfaBody = styled.div`
  color: rgba(26, 26, 26, 0.86);
  font-size: 0.84rem;
  line-height: 1.4;
`;

const MfaPill = styled.span<{ $enabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.76rem;
  font-weight: 900;
  background: ${({ $enabled }) =>
    $enabled ? 'rgba(16, 185, 129, 0.2)' : 'rgba(127, 29, 29, 0.14)'};
  color: ${({ $enabled }) => ($enabled ? '#065f46' : '#7f1d1d')};
  border: 1px solid ${({ $enabled }) =>
    $enabled ? 'rgba(16, 185, 129, 0.48)' : 'rgba(127, 29, 29, 0.3)'};
`;

const MfaModalHint = styled.div`
  color: #cdd3df;
  font-size: 0.82rem;
  line-height: 1.42;
`;

const MfaQrPreview = styled.div`
  height: 168px;
  border-radius: 10px;
  padding: 6px;
  display: grid;
  place-items: center;
`;

const MfaKey = styled.code`
  display: block;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.04);
  color: #ffd8b5;
  font-size: 0.78rem;
  line-height: 1.45;
  word-break: break-all;
`;

const PasswordModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  z-index: 110;
  background: rgba(0, 0, 0, 0.56);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
`;

const PasswordModal = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: ${({ $open }) =>
    $open ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -48%) scale(0.98)'};
  width: min(520px, calc(100vw - 28px));
  z-index: 111;
  border-radius: 16px;
  border: 1px solid rgba(26, 26, 26, 0.42);
  background: linear-gradient(180deg, #1b1c27 0%, #141622 100%);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.4);
  padding: ${({ theme }) => theme.spacing(2)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.2)};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease, transform 180ms ease;
`;

const PasswordModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const PasswordModalTitle = styled.h3`
  margin: 0;
  color: #e8eaed;
  font-size: 1.15rem;
  font-weight: 900;
`;

const PasswordModalClose = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: #d6d9e0;
  border-radius: 10px;
  min-height: 34px;
  min-width: 34px;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
`;

const PasswordForm = styled.form`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.9)};
`;

const PasswordField = styled.label`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const PasswordLabel = styled.span`
  color: #cdd3df;
  font-size: 0.84rem;
  font-weight: 800;
`;

const PasswordInput = styled.input`
  width: 100%;
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.05);
  color: #e8eaed;
  font-size: 0.94rem;
  padding: 0 12px;

  &::placeholder {
    color: rgba(232, 234, 237, 0.55);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.72);
  }
`;

const PasswordActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(0.6)};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const PasswordSecondaryButton = styled.button`
  border-radius: 10px;
  min-height: 38px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: #d7dbe3;
  font-size: 0.84rem;
  font-weight: 800;
  padding: 0 12px;
  cursor: pointer;
`;

const PasswordPrimaryButton = styled.button`
  border-radius: 10px;
  min-height: 38px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: ${backendPalette.black};
  color: ${backendPalette.red};
  font-size: 0.84rem;
  font-weight: 900;
  padding: 0 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PasswordModalMessage = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#ff8d8d' : '#bfffd6')};
  font-size: 0.82rem;
  font-weight: 700;
`;

function getProfileInitials(formData: EditableProfileForm): string {
  const first = formData.firstName.trim();
  const last = formData.lastName.trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();

  const safe = formData.email || 'user@example.com';
  const handle = safe.split('@')[0];
  const parts = handle.split('.');
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return handle.slice(0, 2).toUpperCase();
}

function initialsFromLabel(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  if (!text) return 'B';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase() || 'B';
}

function toOrgBusinessForm(profile: ClientOrgBusinessProfile | null): OrgBusinessForm {
  return {
    businessName: profile?.businessName ?? '',
    logoURL: profile?.logoURL ?? '',
    logoStoragePath: profile?.logoStoragePath ?? '',
    contactName: profile?.contactName ?? '',
    contactEmail: profile?.contactEmail ?? '',
    contactPhone: formatUsPhone(profile?.contactPhone ?? ''),
    website: profile?.website ?? '',
    address: profile?.address ?? '',
  };
}

function normalizeRole(
  role: string | null | undefined,
): 'client' | 'agent' | 'admin' | 'candidate' {
  if (role === 'admin' || role === 'agent' || role === 'candidate') return role;
  return 'client';
}

function planLabel(planId: string | null, status: string | null): string {
  if (!status || (status !== 'active' && status !== 'trialing')) return PLAN_NAME.free;
  return PLAN_NAME[planId || ''] || (planId && !planId.startsWith('price_') ? planId : 'Paid');
}

function toUnixTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function subscriptionPlanLabel(
  snapshot: SubSnapshot | null,
  fallbackPlanId: string | null,
  status: string | null,
): string {
  if (!status || (status !== 'active' && status !== 'trialing')) return PLAN_NAME.free;
  const directDisplay =
    typeof snapshot?.planDisplayName === 'string' ? snapshot.planDisplayName.trim() : '';
  if (directDisplay) return directDisplay;

  const metadataPlanName =
    snapshot?.metadata && typeof (snapshot.metadata as any).planName === 'string'
      ? String((snapshot.metadata as any).planName).trim()
      : '';
  if (metadataPlanName) return metadataPlanName;

  const rawNickname = snapshot?.items?.data?.[0]?.price?.nickname;
  const nickname = typeof rawNickname === 'string' ? rawNickname.trim() : '';
  if (nickname) return nickname;

  const product = snapshot?.items?.data?.[0]?.price?.product;
  const productName =
    product && typeof product === 'object' && typeof product.name === 'string'
      ? product.name.trim()
      : '';
  if (productName) return productName;

  const candidatePlanId = snapshot?.planId ?? fallbackPlanId;
  return planLabel(candidatePlanId, status);
}

function profileDisplayName(formData: EditableProfileForm): string {
  const full = `${formData.firstName} ${formData.lastName}`.trim();
  if (full) return full;
  return formData.email.split('@')[0] || 'User';
}

function renewalText(snapshot: SubSnapshot | null): string | null {
  if (!snapshot) return null;
  const active = snapshot.status === 'active' || snapshot.status === 'trialing';
  if (!active) return null;
  const ts =
    toUnixTimestamp(snapshot.nextBillingAt) ??
    toUnixTimestamp(snapshot.cancel_at) ??
    (snapshot.status === 'trialing'
      ? toUnixTimestamp(snapshot.trial_end)
      : toUnixTimestamp(snapshot.current_period_end));
  if (!ts) return null;
  const date = new Date(ts * 1000).toLocaleDateString();
  return toUnixTimestamp(snapshot.cancel_at) ? `Cancels on ${date}` : `Renews on ${date}`;
}

function friendlyPasswordMessage(error: any): string {
  const code = String(error?.code ?? '');
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Current password is incorrect.';
    case 'auth/weak-password':
      return 'New password is too weak.';
    case 'auth/requires-recent-login':
      return 'Please sign in again, then retry password change.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait and try again.';
    default:
      return String(error?.message ?? 'Unable to change password right now.');
  }
}

function friendlyMfaMessage(error: any): string {
  const code = String(error?.code ?? '');
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Current password is incorrect.';
    case 'auth/invalid-verification-code':
      return 'The authenticator code is invalid. Please try again.';
    case 'auth/code-expired':
      return 'That authenticator code expired. Use a fresh code and retry.';
    case 'auth/missing-verification-code':
      return 'Enter the 6-digit code from your authenticator app.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait and try again.';
    case 'auth/requires-recent-login':
      return 'Please sign in again before changing two-factor settings.';
    case 'auth/operation-not-allowed':
      return 'Authenticator MFA is not enabled for this Firebase project yet. Enable TOTP MFA in project auth settings first.';
    default:
      return String(error?.message ?? 'Unable to update two-factor settings right now.');
  }
}

const API_BASE = (process.env.REACT_APP_API_BASE ?? '').trim();
const USER_ME_CACHE_TTL_MS = 5 * 60 * 1000;
let cachedUserMeProfile: UserMeResponse | null = null;
let cachedUserMeFetchedAt = 0;

export default function ProfilePage() {
  const { user, role, organizations, orgId, refreshRole } = useAuth();
  const queryClient = useQueryClient();
  const authDisplayName = (user?.displayName ?? '').trim();
  const authNameParts = authDisplayName.split(/\s+/).filter(Boolean);
  const authFirstName = authNameParts[0] ?? '';
  const authLastName = authNameParts.slice(1).join(' ');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [memberSince, setMemberSince] = useState('--');
  const [profileRole, setProfileRole] = useState<string>(role || 'client');
  const [agentTitle, setAgentTitle] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [sub, setSub] = useState<SubSnapshot | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [mfaTotpFactors, setMfaTotpFactors] = useState<MultiFactorInfo[]>([]);
  const [mfaStatusMessage, setMfaStatusMessage] = useState<string | null>(null);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaSetupStep, setMfaSetupStep] = useState<'reauth' | 'verify'>('reauth');
  const [mfaSetupPassword, setMfaSetupPassword] = useState('');
  const [mfaSetupOtp, setMfaSetupOtp] = useState('');
  const [mfaSetupSecret, setMfaSetupSecret] = useState<TotpSecret | null>(null);
  const [mfaSetupQrUrl, setMfaSetupQrUrl] = useState<string>('');
  const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
  const [mfaSetupError, setMfaSetupError] = useState<string | null>(null);
  const [mfaDisableOpen, setMfaDisableOpen] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaDisableLoading, setMfaDisableLoading] = useState(false);
  const [mfaDisableError, setMfaDisableError] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [discordStatus, setDiscordStatus] = useState<DiscordLinkStatus | null>(null);
  const [discordLoading, setDiscordLoading] = useState(false);
  const [discordAction, setDiscordAction] = useState<'connect' | 'unlink' | null>(null);
  const [discordNotice, setDiscordNotice] = useState<string | null>(null);
  const [discordError, setDiscordError] = useState<string | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);
  const [orgBusinessProfile, setOrgBusinessProfile] = useState<ClientOrgBusinessProfile | null>(null);
  const [orgBusinessForm, setOrgBusinessForm] = useState<OrgBusinessForm>(
    toOrgBusinessForm(null),
  );
  const [orgBusinessLoading, setOrgBusinessLoading] = useState(false);
  const [orgBusinessSaving, setOrgBusinessSaving] = useState(false);
  const [orgBusinessEditMode, setOrgBusinessEditMode] = useState(false);
  const [orgBuilderStarted, setOrgBuilderStarted] = useState(false);
  const [orgBusinessNotice, setOrgBusinessNotice] = useState<string | null>(null);
  const [orgBusinessError, setOrgBusinessError] = useState<string | null>(null);
  const [orgLogoUploading, setOrgLogoUploading] = useState(false);
  const orgLogoFileInputRef = useRef<HTMLInputElement | null>(null);

  const [formData, setFormData] = useState<EditableProfileForm>({
    firstName: authFirstName,
    lastName: authLastName,
    photoURL: user?.photoURL ?? '',
    photoStoragePath: '',
    userType: 'individual',
    contactPhone: formatUsPhone(''),
    portfolioWebsite: '',
    email: user?.email || '',
    businessWebsite: '',
    team: '',
    title: '',
    supervisorName: '',
    bio: '',
  });

  const normalizedRole = normalizeRole(profileRole);
  const isCandidateProfile = normalizedRole === 'candidate';
  const isClientProfile = normalizedRole === 'client';
  const isClientLikeProfile = isClientProfile || isCandidateProfile;
  const showOrganizationProfileCard = false;
  const isInternalProfile =
    normalizedRole === 'agent' || normalizedRole === 'admin';
  const mfaEnabled = mfaTotpFactors.length > 0;
  const organizationNames = useMemo(
    () =>
      organizations
        .map((organization) => organization.businessName || organization.orgName)
        .map((name) => String(name || '').trim())
        .filter(Boolean),
    [organizations],
  );
  const organizationMembershipLabel =
    organizationNames.length > 0 ? organizationNames.join(', ') : '--';
  const activeOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.orgId === (orgId ?? ''))
      ?? organizations[0]
      ?? null,
    [orgId, organizations],
  );
  const hasOrganizationProfile = useMemo(() => {
    if (!orgBusinessProfile) return false;
    const businessName = (orgBusinessProfile.businessName ?? '').trim();
    return orgBusinessProfile.organizationType === 'business' || businessName.length > 0;
  }, [orgBusinessProfile]);
  const showOrgSetupPrompt = Boolean(
    showOrganizationProfileCard
    && orgBusinessProfile
    && !orgBusinessLoading
    && !hasOrganizationProfile
    && !orgBuilderStarted,
  );
  const orgBusinessFormBaseline = useMemo(
    () => toOrgBusinessForm(orgBusinessProfile),
    [orgBusinessProfile],
  );
  const orgBusinessDirty = useMemo(
    () =>
      orgBusinessForm.businessName !== orgBusinessFormBaseline.businessName
      || orgBusinessForm.logoURL !== orgBusinessFormBaseline.logoURL
      || orgBusinessForm.logoStoragePath !== orgBusinessFormBaseline.logoStoragePath
      || orgBusinessForm.contactName !== orgBusinessFormBaseline.contactName
      || orgBusinessForm.contactEmail !== orgBusinessFormBaseline.contactEmail
      || orgBusinessForm.contactPhone !== orgBusinessFormBaseline.contactPhone
      || orgBusinessForm.website !== orgBusinessFormBaseline.website
      || orgBusinessForm.address !== orgBusinessFormBaseline.address,
    [orgBusinessForm, orgBusinessFormBaseline],
  );

  const showFieldValue = (value: string): string => {
    if (editing) return value;
    return value.trim().length > 0 ? value : '--';
  };

  const isEmptyField = (value: string): boolean => value.trim().length === 0;
  const invalidateOnboardingTasks = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['onboarding'] });
  }, [queryClient]);

  const loadDiscordStatus = useCallback(async () => {
    if (!auth.currentUser) {
      setDiscordStatus(null);
      setDiscordLoading(false);
      return;
    }

    setDiscordLoading(true);
    try {
      const next = await fetchDiscordLinkStatus();
      setDiscordStatus(next);
      setDiscordError(null);
    } catch (error: any) {
      setDiscordError(error?.message || 'Failed to load Discord account status.');
    } finally {
      setDiscordLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const hydrateProfile = (
      data: UserMeResponse,
      currentUser: { email?: string | null; photoURL?: string | null },
    ) => {
      const resolvedPhotoURL = resolvePhotoURL(data);
      setFormData((prev) => ({
        ...prev,
        firstName: data.firstName ?? prev.firstName,
        lastName: data.lastName ?? prev.lastName,
        photoURL: resolvedPhotoURL ?? currentUser.photoURL ?? prev.photoURL,
        photoStoragePath: data.photoStoragePath ?? prev.photoStoragePath,
        userType: (data.userType ?? prev.userType) || 'individual',
        contactPhone: formatUsPhone(data.contactPhone ?? data.businessPhone ?? prev.contactPhone),
        portfolioWebsite: data.portfolioWebsite ?? prev.portfolioWebsite,
        email: data.email ?? currentUser.email ?? prev.email,
        businessWebsite: data.businessWebsite ?? prev.businessWebsite,
        team: data.team ?? prev.team,
        title: data.title ?? data.agentTitle ?? prev.title,
        supervisorName: data.supervisorName ?? prev.supervisorName,
        bio: data.bio ?? prev.bio,
      }));
      setProfileRole(data.role ?? role ?? 'client');
      setAgentTitle(data.title ?? data.agentTitle ?? null);
      setSubscriptionStatus(data.subscriptionStatus ?? null);
      setPlanId(data.planId ?? null);
      setMemberSince(formatMonthYear(toDate(data.createdAt)));
    };

    (async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        if (mounted) setLoading(false);
        return;
      }

      setLoading(true);
      const now = Date.now();
      const hasFreshCache =
        cachedUserMeProfile !== null && now - cachedUserMeFetchedAt < USER_ME_CACHE_TTL_MS;
      if (hasFreshCache && mounted) {
        hydrateProfile(cachedUserMeProfile as UserMeResponse, currentUser);
        setLoading(false);
      }

      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API_BASE}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await res.json()) as UserMeResponse;
        if (!mounted) return;
        cachedUserMeProfile = data;
        cachedUserMeFetchedAt = Date.now();
        hydrateProfile(data, currentUser);
      } catch {
        if (mounted && !hasFreshCache) setMemberSince('--');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [role, user?.email, user?.uid]);

  useEffect(() => {
    if (!user) {
      setDiscordStatus(null);
      setDiscordLoading(false);
      setDiscordNotice(null);
      setDiscordError(null);
      setDiscordAction(null);
      return;
    }

    void loadDiscordStatus();
  }, [loadDiscordStatus, user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('discord');
    if (!status) return;

    const message = params.get('discordMessage');
    if (status === 'linked') {
      setDiscordNotice(message || 'Discord account connected.');
      setDiscordError(null);
      invalidateOnboardingTasks();
      void loadDiscordStatus();
    } else if (status === 'unlinked') {
      setDiscordNotice(message || 'Discord account disconnected.');
      setDiscordError(null);
      invalidateOnboardingTasks();
      void loadDiscordStatus();
    } else if (status === 'error') {
      setDiscordError(message || 'Discord linking failed.');
    }

    params.delete('discord');
    params.delete('discordMessage');
    const nextSearch = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`,
    );
  }, [invalidateOnboardingTasks, loadDiscordStatus]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        // Regression guard: subscription endpoint lives under Billing.
        // Do NOT change this back to `/api/users/me/subscription` (removed; 404s).
        const res = await fetch(`${API_BASE}/api/billing/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!mounted) return;
        setSub((data?.subscription ?? null) as SubSnapshot | null);
      } catch {
        if (mounted) setSub(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!showOrganizationProfileCard) {
      setOrgBusinessProfile(null);
      setOrgBusinessForm(toOrgBusinessForm(null));
      setOrgBusinessEditMode(false);
      setOrgBuilderStarted(false);
      setOrgBusinessError(null);
      setOrgBusinessNotice(null);
      setOrgBusinessLoading(false);
      return;
    }

    const targetOrgId = orgId ?? organizations[0]?.orgId;
    if (!targetOrgId) {
      setOrgBusinessProfile(null);
      setOrgBusinessForm(toOrgBusinessForm(null));
      setOrgBusinessEditMode(false);
      setOrgBuilderStarted(false);
      setOrgBusinessError(null);
      setOrgBusinessNotice(null);
      setOrgBusinessLoading(false);
      return;
    }

    let mounted = true;
    setOrgBusinessLoading(true);
    setOrgBusinessError(null);
    setOrgBusinessNotice(null);

    (async () => {
      try {
        const next = await fetchClientOrgBusinessProfile(targetOrgId);
        if (!mounted) return;
        setOrgBusinessProfile(next);
        setOrgBusinessForm(toOrgBusinessForm(next));
        setOrgBusinessEditMode(false);
        setOrgBuilderStarted(false);
      } catch (error: any) {
        if (!mounted) return;
        setOrgBusinessProfile(null);
        setOrgBusinessForm(toOrgBusinessForm(null));
        setOrgBusinessEditMode(false);
        setOrgBuilderStarted(false);
        setOrgBusinessError(error?.message || 'Failed to load organization profile.');
      } finally {
        if (mounted) {
          setOrgBusinessLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [showOrganizationProfileCard, orgId, organizations]);

  useEffect(() => {
    if (!passwordModalOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !passwordSaving) {
        setPasswordModalOpen(false);
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [passwordModalOpen, passwordSaving]);

  const refreshMfaFactors = () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setMfaTotpFactors([]);
      return;
    }
    const factors = multiFactor(currentUser).enrolledFactors.filter(
      (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    setMfaTotpFactors(factors);
  };

  useEffect(() => {
    refreshMfaFactors();
  }, [user?.uid]);

  useEffect(() => {
    if (!mfaSetupOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (mfaSetupLoading) return;
      setMfaSetupOpen(false);
      setMfaSetupStep('reauth');
      setMfaSetupPassword('');
      setMfaSetupOtp('');
      setMfaSetupSecret(null);
      setMfaSetupQrUrl('');
      setMfaSetupError(null);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [mfaSetupOpen, mfaSetupLoading]);

  useEffect(() => {
    if (!mfaDisableOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (mfaDisableLoading) return;
      setMfaDisableOpen(false);
      setMfaDisablePassword('');
      setMfaDisableError(null);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [mfaDisableOpen, mfaDisableLoading]);

  const openMfaSetup = () => {
    setMfaStatusMessage(null);
    setMfaSetupOpen(true);
    setMfaSetupStep('reauth');
    setMfaSetupPassword('');
    setMfaSetupOtp('');
    setMfaSetupSecret(null);
    setMfaSetupQrUrl('');
    setMfaSetupError(null);
  };

  const closeMfaSetup = (force = false) => {
    if (mfaSetupLoading && !force) return;
    setMfaSetupOpen(false);
    setMfaSetupStep('reauth');
    setMfaSetupPassword('');
    setMfaSetupOtp('');
    setMfaSetupSecret(null);
    setMfaSetupQrUrl('');
    setMfaSetupError(null);
  };

  const openMfaDisable = () => {
    setMfaStatusMessage(null);
    setMfaDisableOpen(true);
    setMfaDisablePassword('');
    setMfaDisableError(null);
  };

  const closeMfaDisable = (force = false) => {
    if (mfaDisableLoading && !force) return;
    setMfaDisableOpen(false);
    setMfaDisablePassword('');
    setMfaDisableError(null);
  };

  const handleMfaStartSetup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mfaSetupLoading) return;
    const currentUser = auth.currentUser;
    if (!currentUser?.email) {
      setMfaSetupError('Current account does not support authenticator setup.');
      return;
    }
    if (!mfaSetupPassword.trim()) {
      setMfaSetupError('Enter your current password to continue.');
      return;
    }

    try {
      setMfaSetupLoading(true);
      setMfaSetupError(null);
      const credential = EmailAuthProvider.credential(currentUser.email, mfaSetupPassword);
      await reauthenticateWithCredential(currentUser, credential);
      const mfaSession = await multiFactor(currentUser).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(mfaSession);
      const accountName = (formData.email || currentUser.email || currentUser.uid).trim();
      const qrUrl = secret.generateQrCodeUrl(accountName, 'Cybard');
      setMfaSetupSecret(secret);
      setMfaSetupQrUrl(qrUrl);
      setMfaSetupStep('verify');
      setMfaSetupPassword('');
    } catch (error: any) {
      setMfaSetupError(friendlyMfaMessage(error));
    } finally {
      setMfaSetupLoading(false);
    }
  };

  const handleMfaVerifyAndEnable = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mfaSetupLoading) return;
    const currentUser = auth.currentUser;
    if (!currentUser || !mfaSetupSecret) {
      setMfaSetupError('Authenticator setup session expired. Start setup again.');
      setMfaSetupStep('reauth');
      return;
    }

    const otp = mfaSetupOtp.trim();
    if (otp.length < 6) {
      setMfaSetupError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    try {
      setMfaSetupLoading(true);
      setMfaSetupError(null);
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(mfaSetupSecret, otp);
      const factorLabel = `${profileDisplayName(formData)} authenticator`;
      await multiFactor(currentUser).enroll(assertion, factorLabel);
      refreshMfaFactors();
      setMfaStatusMessage('Authenticator app 2FA is now enabled for your account.');
      closeMfaSetup(true);
    } catch (error: any) {
      setMfaSetupError(friendlyMfaMessage(error));
    } finally {
      setMfaSetupLoading(false);
    }
  };

  const handleMfaDisable = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mfaDisableLoading) return;
    const currentUser = auth.currentUser;
    if (!currentUser?.email) {
      setMfaDisableError('Current account does not support authenticator updates.');
      return;
    }
    if (!mfaDisablePassword.trim()) {
      setMfaDisableError('Enter your current password to continue.');
      return;
    }

    try {
      setMfaDisableLoading(true);
      setMfaDisableError(null);
      const credential = EmailAuthProvider.credential(currentUser.email, mfaDisablePassword);
      await reauthenticateWithCredential(currentUser, credential);
      const totpFactors = multiFactor(currentUser).enrolledFactors.filter(
        (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      for (const factor of totpFactors) {
        await multiFactor(currentUser).unenroll(factor.uid);
      }
      refreshMfaFactors();
      setMfaStatusMessage('Authenticator app 2FA has been disabled.');
      closeMfaDisable(true);
    } catch (error: any) {
      setMfaDisableError(friendlyMfaMessage(error));
    } finally {
      setMfaDisableLoading(false);
    }
  };

  const openAvatarFilePicker = () => {
    if (avatarUploading) return;
    avatarFileInputRef.current?.click();
  };

  const handleAvatarFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setAvatarError(null);
    setSaved(false);
    setSaveError(null);

    if (!ACCEPTED_AVATAR_MIME_TYPES.has(file.type)) {
      setAvatarError('Please upload a JPG, PNG, or WEBP image.');
      return;
    }
    if (file.size > MAX_AVATAR_FILE_SIZE_BYTES) {
      setAvatarError('Profile image must be 5MB or smaller.');
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setAvatarError('You must be signed in to update your avatar.');
        return;
      }

      setAvatarUploading(true);
      const extension = extensionFromMimeType(file.type);
      const objectPath = `profile-images/${currentUser.uid}/avatar_${Date.now()}.${extension}`;
      const objectRef = storageRef(storage, objectPath);

      await uploadBytes(objectRef, file, {
        contentType: file.type,
        cacheControl: 'public,max-age=31536000',
      });

      const downloadURL = await getDownloadURL(objectRef);
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          photoURL: downloadURL,
          photoStoragePath: objectPath,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to save profile picture.');
      }

      const data = (await res.json()) as UserMeResponse;
      const resolvedPhotoURL = resolvePhotoURL(data) ?? downloadURL;
      setFormData((prev) => ({
        ...prev,
        photoURL: resolvedPhotoURL,
        photoStoragePath: data.photoStoragePath ?? objectPath,
      }));
      cachedUserMeProfile = {
        ...(cachedUserMeProfile ?? {}),
        ...data,
        photoURL: resolvedPhotoURL,
        photoStoragePath: data.photoStoragePath ?? objectPath,
      };
      cachedUserMeFetchedAt = Date.now();
      await updateProfile(currentUser, { photoURL: resolvedPhotoURL }).catch(() => null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setAvatarError('Could not upload profile picture. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;

    setSaving(true);
    setSaved(false);
    setSaveError(null);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const token = await currentUser.getIdToken();

      if (isClientLikeProfile) {
        const missingBaseField =
          !formData.firstName.trim() ||
          !formData.lastName.trim() ||
          !formData.email.trim();
        const missingClientOnlyField =
          normalizedRole === 'client' &&
          !formData.contactPhone.trim();

        if (missingBaseField || missingClientOnlyField) {
          setSaveError(
            normalizedRole === 'candidate'
              ? 'Candidate profiles require first name, last name, and email.'
              : 'Client profiles require first name, last name, and contact phone.',
          );
          return;
        }
      }

      if (formData.contactPhone.trim() && !isValidUsPhone(formData.contactPhone)) {
        setSaveError('Please enter a valid 10-digit phone number.');
        return;
      }

      if (isInternalProfile) {
        const missingInternalField =
          !formData.photoURL.trim() ||
          !formData.firstName.trim() ||
          !formData.lastName.trim() ||
          !formData.bio.trim();
        if (missingInternalField) {
          setSaveError(
            'Agent/Admin profiles require profile picture URL, first name, last name, and bio.',
          );
          return;
        }
      }

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        photoURL: formData.photoURL,
        userType: isClientLikeProfile ? undefined : formData.userType || null,
        contactPhone: formatUsPhone(formData.contactPhone),
        portfolioWebsite: formData.portfolioWebsite,
        bio: formData.bio,
        businessWebsite: formData.businessWebsite,
      };

      const res = await fetch(`${API_BASE}/api/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as UserMeResponse;
      const resolvedPhotoURL = resolvePhotoURL(data);
      if (!res.ok) {
        throw new Error('Failed to save profile');
      }

      setFormData((prev) => ({
        ...prev,
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        photoURL: resolvedPhotoURL ?? '',
        photoStoragePath: data.photoStoragePath ?? '',
        userType: data.userType ?? prev.userType ?? 'individual',
        contactPhone: formatUsPhone(data.contactPhone ?? data.businessPhone ?? ''),
        portfolioWebsite: data.portfolioWebsite ?? '',
        email: data.email ?? prev.email,
        businessWebsite: data.businessWebsite ?? '',
        team: data.team ?? '',
        title: data.title ?? data.agentTitle ?? '',
        supervisorName: data.supervisorName ?? '',
        bio: data.bio ?? '',
      }));
      cachedUserMeProfile = { ...(cachedUserMeProfile ?? {}), ...data };
      cachedUserMeFetchedAt = Date.now();
      const resolvedAuthDisplayName =
        `${data.firstName ?? formData.firstName} ${data.lastName ?? formData.lastName}`.trim() ||
        data.displayName ||
        null;
      dispatchUserProfileUpdated({
        email: data.email ?? formData.email ?? currentUser.email ?? null,
        displayName: data.displayName ?? null,
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        role: data.role ?? profileRole ?? null,
        title: data.title ?? data.agentTitle ?? null,
        agentTitle: data.agentTitle ?? data.title ?? null,
        photoURL: resolvedPhotoURL ?? null,
      });
      const authProfileUpdates: { displayName?: string; photoURL?: string } = {};
      if (resolvedAuthDisplayName) authProfileUpdates.displayName = resolvedAuthDisplayName;
      if (resolvedPhotoURL) authProfileUpdates.photoURL = resolvedPhotoURL;
      if (Object.keys(authProfileUpdates).length > 0) {
        await updateProfile(currentUser, authProfileUpdates).catch(() => null);
      }
      setProfileRole(data.role ?? profileRole);
      setAgentTitle(data.title ?? data.agentTitle ?? null);
      setSubscriptionStatus(data.subscriptionStatus ?? subscriptionStatus);
      setPlanId(data.planId ?? planId);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setSaveError('Could not save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveOrganizationProfile = async () => {
    if (!orgBusinessProfile?.canEdit) return;

    const missingRequiredLabels: string[] = [];
    if (!orgBusinessForm.businessName.trim()) missingRequiredLabels.push('Business name');
    if (!orgBusinessForm.contactEmail.trim()) missingRequiredLabels.push('Business contact email');
    if (!orgBusinessForm.contactPhone.trim()) missingRequiredLabels.push('Business contact phone');
    if (missingRequiredLabels.length > 0) {
      setOrgBusinessError(`Required fields: ${missingRequiredLabels.join(', ')}.`);
      return;
    }

    try {
      setOrgBusinessSaving(true);
      setOrgBusinessError(null);
      setOrgBusinessNotice(null);
      const next = await updateClientOrgBusinessProfile({
        orgId: orgId ?? activeOrganization?.orgId ?? undefined,
        organizationType: hasOrganizationProfile ? undefined : 'business',
        businessName: orgBusinessForm.businessName,
        logoURL: orgBusinessForm.logoURL,
        logoStoragePath: orgBusinessForm.logoStoragePath,
        contactName: orgBusinessForm.contactName,
        contactEmail: orgBusinessForm.contactEmail,
        contactPhone: orgBusinessForm.contactPhone,
        website: orgBusinessForm.website,
        address: orgBusinessForm.address,
      });
      setOrgBusinessProfile(next);
      setOrgBusinessForm(toOrgBusinessForm(next));
      setOrgBusinessEditMode(false);
      setOrgBuilderStarted(false);
      setOrgBusinessNotice('Organization profile saved.');
      await refreshRole();
    } catch (error: any) {
      setOrgBusinessError(error?.message || 'Failed to save organization profile.');
    } finally {
      setOrgBusinessSaving(false);
    }
  };

  const openOrgLogoFilePicker = () => {
    if (!orgBusinessProfile?.canEdit || orgLogoUploading) return;
    orgLogoFileInputRef.current?.click();
  };

  const handleOrgLogoFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user || !orgBusinessProfile?.canEdit) return;

    if (!ACCEPTED_ORG_LOGO_MIME_TYPES.has(file.type)) {
      setOrgBusinessError('Business logo must be PNG, JPG, or WEBP.');
      return;
    }
    if (file.size > MAX_ORG_LOGO_FILE_SIZE_BYTES) {
      setOrgBusinessError('Business logo must be 5MB or smaller.');
      return;
    }

    const extension = extensionFromMimeType(file.type);
    const safeOrgId = (orgId ?? activeOrganization?.orgId ?? user.uid).replace(
      /[^a-zA-Z0-9_-]/g,
      '_',
    );
    const objectPath = `org-logos/${safeOrgId}/${Date.now()}.${extension}`;

    try {
      setOrgLogoUploading(true);
      setOrgBusinessError(null);
      setOrgBusinessNotice(null);

      const objectRef = storageRef(storage, objectPath);
      await uploadBytes(objectRef, file, {
        contentType: file.type,
        cacheControl: 'public,max-age=3600',
      });
      const logoURL = await getDownloadURL(objectRef);
      const next = await updateClientOrgBusinessProfile({
        orgId: orgId ?? activeOrganization?.orgId ?? undefined,
        logoURL,
        logoStoragePath: objectPath,
      });
      setOrgBusinessProfile(next);
      setOrgBusinessForm((prev) => ({
        ...prev,
        logoURL: next.logoURL ?? '',
        logoStoragePath: next.logoStoragePath ?? '',
      }));
      setOrgBusinessNotice('Business logo updated.');
      await refreshRole();
    } catch (error: any) {
      setOrgBusinessError(error?.message || 'Failed to upload business logo.');
    } finally {
      setOrgLogoUploading(false);
    }
  };

  const closePasswordModal = () => {
    if (passwordSaving) return;
    setPasswordModalOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (passwordSaving) return;
    setPasswordError(null);
    setPasswordSuccess(null);

    const current = currentPassword.trim();
    if (!current) {
      setPasswordError('Current password is required.');
      return;
    }
    const passwordPolicyError = getPasswordPolicyError(newPassword);
    if (passwordPolicyError) {
      setPasswordError(passwordPolicyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation must match.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password.');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser?.email) {
      setPasswordError('Current account does not support password updates.');
      return;
    }

    try {
      setPasswordSaving(true);
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword,
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordModalOpen(false);
    } catch (error: any) {
      setPasswordError(friendlyPasswordMessage(error));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleConnectDiscord = async () => {
    if (discordAction) return;
    try {
      setDiscordAction('connect');
      setDiscordNotice(null);
      setDiscordError(null);
      const next = await requestDiscordAuthorizeUrl('/dashboard/profile');
      window.location.assign(next.authorizeUrl);
    } catch (error: any) {
      setDiscordError(error?.message || 'Failed to start Discord connection.');
      setDiscordAction(null);
    }
  };

  const handleUnlinkDiscord = async () => {
    if (discordAction) return;
    const confirmed = window.confirm(
      'Disconnect this Discord account from Cybard and remove its private-room access?',
    );
    if (!confirmed) return;

    try {
      setDiscordAction('unlink');
      setDiscordNotice(null);
      setDiscordError(null);
      await unlinkDiscordAccount();
      await loadDiscordStatus();
      invalidateOnboardingTasks();
      setDiscordNotice('Discord account disconnected.');
    } catch (error: any) {
      setDiscordError(error?.message || 'Failed to unlink Discord account.');
    } finally {
      setDiscordAction(null);
    }
  };

  const discordAccountValue = discordLoading
    ? 'Loading Discord account...'
    : discordStatus?.connected
    ? `@${discordStatus.link?.username ?? discordStatus.link?.displayName ?? 'discord'}`
    : 'Not connected';
  const discordLinkedDisplayName =
    (discordStatus?.link?.displayName ?? discordStatus?.link?.username ?? '').trim();
  const discordNeedsReconnect = Boolean(
    discordStatus?.connected && discordStatus.link?.guildJoined === false,
  );
  const discordHelperTone: 'default' | 'success' | 'error' =
    discordError
      ? 'error'
      : discordNotice
      ? 'success'
      : 'default';
  const discordHelperText = discordError
    ? discordError
    : discordNotice
    ? discordNotice
    : !discordStatus?.enabled
    ? 'Discord workspaces are not configured for this environment yet.'
    : discordLoading
    ? 'Checking your Discord identity and server access.'
    : discordNeedsReconnect
    ? 'Your Discord account is linked but is not currently in the Cybard Discord server. Use Open community to join again, or disconnect and reconnect if the link looks wrong.'
    : discordStatus.connected
    ? `Linked as ${discordLinkedDisplayName || 'your Discord account'}. Community and private-room access are handled automatically.`
    : 'Connect your Discord account to access your organization private room inside the Cybard Discord server.';

  return (
    <Wrap>
      <Header>
        <Heading>Profile Settings</Heading>
        <Subtitle>
          Keep your profile aligned with Users/CRM details. Bio stays private to your
          settings page.
        </Subtitle>
      </Header>

      <Layout>
        <SideCard variants={fadeInUp} initial="hidden" animate="visible">
          <Avatar>
            {formData.photoURL ? (
              <AvatarImage
                src={formData.photoURL}
                alt={profileDisplayName(formData)}
                loading="eager"
              />
            ) : (
              getProfileInitials(formData)
            )}
            <AvatarEditButton
              type="button"
              onClick={openAvatarFilePicker}
              disabled={avatarUploading}
              aria-label="Edit profile picture"
              title={avatarUploading ? 'Uploading...' : 'Edit profile picture'}
            >
              <Pencil size={14} />
            </AvatarEditButton>
          </Avatar>
          <HiddenFileInput
            ref={avatarFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarFileSelected}
          />
          <Name>{profileDisplayName(formData)}</Name>
          <Email>{formData.email}</Email>
          <RoleBadge $role={normalizedRole}>{normalizedRole}</RoleBadge>
          {avatarError ? <SecurityStatus $error>{avatarError}</SecurityStatus> : null}

          <InfoSection>
            <InfoRow>
              <InfoLabel>Member since</InfoLabel>
              <InfoValue>{memberSince}</InfoValue>
            </InfoRow>
            {isClientLikeProfile ? (
              <InfoRow>
                <InfoLabel>Organizations</InfoLabel>
                <InfoValue>{organizationMembershipLabel}</InfoValue>
              </InfoRow>
            ) : null}
            <InfoRow>
              <InfoLabel>Current plan</InfoLabel>
              <InfoValue>
                {subscriptionPlanLabel(
                  sub,
                  planId,
                  sub?.status ?? subscriptionStatus,
                )}
              </InfoValue>
            </InfoRow>
            {renewalText(sub) ? (
              <InfoRow>
                <InfoLabel>Billing</InfoLabel>
                <InfoValue>{renewalText(sub)}</InfoValue>
              </InfoRow>
            ) : null}
            {isInternalProfile && (
              <InfoRow>
                <InfoLabel>Title</InfoLabel>
                <InfoValue>{agentTitle || '--'}</InfoValue>
              </InfoRow>
            )}
            {isInternalProfile && (
              <InfoRow>
                <InfoLabel>Team</InfoLabel>
                <InfoValue>{formData.team || '--'}</InfoValue>
              </InfoRow>
            )}
            {isInternalProfile && (
              <InfoRow>
                <InfoLabel>Supervisor</InfoLabel>
                <InfoValue>{formData.supervisorName || '--'}</InfoValue>
              </InfoRow>
            )}
          </InfoSection>
        </SideCard>

        <Content>
          {saved ? (
            <Notice
              $type="success"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Profile updated successfully.
            </Notice>
          ) : null}
          {saveError ? (
            <Notice
              $type="error"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {saveError}
            </Notice>
          ) : null}

          <ContentCard variants={fadeInUp} initial="hidden" animate="visible">
            <CardHeader>
              <CardTitle>
                {isInternalProfile
                  ? 'Internal Profile Details'
                  : isCandidateProfile
                  ? 'Candidate Profile Details'
                  : 'Client Profile Details'}
              </CardTitle>
              {!editing ? (
                <Button type="button" onClick={() => setEditing(true)}>
                  Edit Profile
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div>Loading...</div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Grid>
                    <Field>
                      <Label>First name</Label>
                      <Input
                        value={showFieldValue(formData.firstName)}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                        }
                        readOnly={!editing}
                        $editing={editing}
                        $empty={!editing && isEmptyField(formData.firstName)}
                        placeholder={editing ? 'First name' : undefined}
                        autoComplete="given-name"
                        tabIndex={editing ? 0 : -1}
                      />
                    </Field>

                    <Field>
                      <Label>Last name</Label>
                      <Input
                        value={showFieldValue(formData.lastName)}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                        }
                        readOnly={!editing}
                        $editing={editing}
                        $empty={!editing && isEmptyField(formData.lastName)}
                        placeholder={editing ? 'Last name' : undefined}
                        autoComplete="family-name"
                        tabIndex={editing ? 0 : -1}
                      />
                    </Field>

                    <Field>
                      <Label>Email</Label>
                      <Input
                        value={showFieldValue(formData.email)}
                        readOnly
                        type="email"
                        $editing={editing}
                        $empty={!editing && isEmptyField(formData.email)}
                        tabIndex={editing ? 0 : -1}
                      />
                    </Field>

                    {isClientLikeProfile ? (
                      <Field>
                        <Label>Organizations</Label>
                        <Input
                          value={organizationMembershipLabel}
                          readOnly
                          $editing={false}
                          $empty={false}
                          tabIndex={-1}
                        />
                      </Field>
                    ) : null}

                    <Field>
                      <Label>
                        {isClientProfile ? 'Contact phone number' : 'Contact phone number (optional)'}
                      </Label>
                      <Input
                        value={showFieldValue(formData.contactPhone)}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            contactPhone: formatUsPhone(e.target.value),
                          }))
                        }
                        readOnly={!editing}
                        $editing={editing}
                        $empty={!editing && isEmptyField(formData.contactPhone)}
                        placeholder={editing ? '(555) 123-4567' : undefined}
                        autoComplete="tel"
                        inputMode="tel"
                        maxLength={14}
                        tabIndex={editing ? 0 : -1}
                      />
                    </Field>

                    {isInternalProfile ? (
                      <>
                        <Field>
                          <Label>Title</Label>
                          <Input
                            value={showFieldValue(formData.title)}
                            readOnly
                            $editing={false}
                            $empty={!formData.title.trim()}
                            tabIndex={-1}
                          />
                        </Field>

                        <Field>
                          <Label>Team</Label>
                          <Input
                            value={showFieldValue(formData.team)}
                            readOnly
                            $editing={false}
                            $empty={!formData.team.trim()}
                            tabIndex={-1}
                          />
                        </Field>

                        <Field>
                          <Label>Supervisor</Label>
                          <Input
                            value={showFieldValue(formData.supervisorName)}
                            readOnly
                            $editing={false}
                            $empty={!formData.supervisorName.trim()}
                            tabIndex={-1}
                          />
                        </Field>

                        <Field>
                          <Label>Portfolio website (optional)</Label>
                          <Input
                            value={showFieldValue(formData.portfolioWebsite)}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                portfolioWebsite: e.target.value,
                              }))
                            }
                            readOnly={!editing}
                            $editing={editing}
                            $empty={!editing && isEmptyField(formData.portfolioWebsite)}
                            placeholder={editing ? 'https://' : undefined}
                            autoComplete="url"
                            tabIndex={editing ? 0 : -1}
                          />
                        </Field>
                      </>
                    ) : null}

                    {isClientLikeProfile ? (
                      <>
                        <Field>
                          <Label>Website (optional)</Label>
                          <Input
                            value={showFieldValue(formData.businessWebsite)}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                businessWebsite: e.target.value,
                              }))
                            }
                            readOnly={!editing}
                            $editing={editing}
                            $empty={!editing && isEmptyField(formData.businessWebsite)}
                            placeholder={editing ? 'https://' : undefined}
                            autoComplete="url"
                            tabIndex={editing ? 0 : -1}
                          />
                        </Field>
                      </>
                    ) : null}
                  </Grid>

                  <Field>
                    <Label>
                      {isInternalProfile ? 'Bio' : 'Bio (optional)'}
                    </Label>
                    <Textarea
                      value={showFieldValue(formData.bio)}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, bio: e.target.value }))
                      }
                      readOnly={!editing}
                      $editing={editing}
                      $empty={!editing && isEmptyField(formData.bio)}
                      placeholder={editing ? 'Tell us about your role or profile.' : undefined}
                      tabIndex={editing ? 0 : -1}
                    />
                  </Field>

                  {editing ? (
                    <Actions>
                      <Button
                        type="button"
                        $variant="secondary"
                        onClick={() => {
                          setEditing(false);
                          setSaveError(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Actions>
                  ) : null}
                </Form>
              )}
            </CardContent>
          </ContentCard>

          {showOrganizationProfileCard ? (
            <ContentCard variants={fadeInUp} initial="hidden" animate="visible">
              <CardHeader>
                <CardTitle>Organization Profile</CardTitle>
                {orgBusinessProfile?.canEdit
                && hasOrganizationProfile
                && !orgBusinessDirty
                && !orgBusinessEditMode ? (
                  <Button
                    type="button"
                    $variant="secondary"
                    onClick={() => setOrgBusinessEditMode(true)}
                  >
                    Edit
                  </Button>
                ) : null}
              </CardHeader>
              <CardContent>
                {orgBusinessError ? (
                  <Notice
                    $type="error"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {orgBusinessError}
                  </Notice>
                ) : null}
                {orgBusinessNotice ? (
                  <Notice
                    $type="success"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {orgBusinessNotice}
                  </Notice>
                ) : null}

                {orgBusinessLoading ? (
                  <div>Loading...</div>
                ) : showOrgSetupPrompt ? (
                  <OrgSetupPanel>
                    <OrgSetupTitle>Build an organization profile</OrgSetupTitle>
                    <OrgSetupBody>
                      Create an organization profile to invite additional contributors and represent your business on tickets and projects.
                    </OrgSetupBody>
                    {orgBusinessProfile?.canEdit ? (
                      <div>
                        <Button
                          type="button"
                          onClick={() => {
                            setOrgBuilderStarted(true);
                            setOrgBusinessEditMode(true);
                            setOrgBusinessError(null);
                            setOrgBusinessNotice(null);
                          }}
                        >
                          Build your organization
                        </Button>
                      </div>
                    ) : (
                      <OrgSetupBody>
                        Only the primary account holder can build an organization profile.
                      </OrgSetupBody>
                    )}
                  </OrgSetupPanel>
                ) : (
                  <>
                    <OrgProfileIntro>
                      Set your organization identity and contact details used throughout client and admin ticket views.
                    </OrgProfileIntro>
                    <OrgProfileRow>
                      <OrgLogoPane>
                        <OrgLogoFrame>
                          <OrgLogo>
                            {orgBusinessForm.logoURL ? (
                              <img src={orgBusinessForm.logoURL} alt="Organization logo" />
                            ) : (
                              initialsFromLabel(
                                orgBusinessForm.businessName
                                || orgBusinessProfile?.orgName
                                || activeOrganization?.businessName
                                || activeOrganization?.orgName
                                || 'Business',
                              )
                            )}
                          </OrgLogo>
                          <OrgLogoEditButton
                            type="button"
                            onClick={openOrgLogoFilePicker}
                            disabled={!orgBusinessProfile?.canEdit || orgLogoUploading}
                          >
                            {orgLogoUploading ? '...' : orgBusinessForm.logoURL ? 'Edit' : 'Add'}
                          </OrgLogoEditButton>
                        </OrgLogoFrame>
                        <HiddenFileInput
                          ref={orgLogoFileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleOrgLogoFileSelected}
                        />
                      </OrgLogoPane>

                      <div>
                        <Grid>
                          <Input
                            $editing={Boolean(orgBusinessProfile?.canEdit && orgBusinessEditMode)}
                            value={orgBusinessForm.businessName}
                            onChange={(event) =>
                              setOrgBusinessForm((prev) => ({
                                ...prev,
                                businessName: event.target.value,
                              }))
                            }
                            readOnly={!orgBusinessEditMode || !orgBusinessProfile?.canEdit}
                            disabled={!orgBusinessProfile?.canEdit}
                            placeholder="Business name *"
                            tabIndex={orgBusinessEditMode && orgBusinessProfile?.canEdit ? 0 : -1}
                          />
                          <Input
                            $editing={Boolean(orgBusinessProfile?.canEdit && orgBusinessEditMode)}
                            value={orgBusinessForm.contactName}
                            onChange={(event) =>
                              setOrgBusinessForm((prev) => ({
                                ...prev,
                                contactName: event.target.value,
                              }))
                            }
                            readOnly={!orgBusinessEditMode || !orgBusinessProfile?.canEdit}
                            disabled={!orgBusinessProfile?.canEdit}
                            placeholder="Industry"
                            tabIndex={orgBusinessEditMode && orgBusinessProfile?.canEdit ? 0 : -1}
                          />
                          <Input
                            $editing={Boolean(orgBusinessProfile?.canEdit && orgBusinessEditMode)}
                            value={orgBusinessForm.contactEmail}
                            onChange={(event) =>
                              setOrgBusinessForm((prev) => ({
                                ...prev,
                                contactEmail: event.target.value,
                              }))
                            }
                            readOnly={!orgBusinessEditMode || !orgBusinessProfile?.canEdit}
                            disabled={!orgBusinessProfile?.canEdit}
                            placeholder="Business contact email *"
                            type="email"
                            tabIndex={orgBusinessEditMode && orgBusinessProfile?.canEdit ? 0 : -1}
                          />
                          <Input
                            $editing={Boolean(orgBusinessProfile?.canEdit && orgBusinessEditMode)}
                            value={orgBusinessForm.contactPhone}
                            onChange={(event) =>
                              setOrgBusinessForm((prev) => ({
                                ...prev,
                                contactPhone: event.target.value,
                              }))
                            }
                            readOnly={!orgBusinessEditMode || !orgBusinessProfile?.canEdit}
                            disabled={!orgBusinessProfile?.canEdit}
                            placeholder="Business contact phone *"
                            tabIndex={orgBusinessEditMode && orgBusinessProfile?.canEdit ? 0 : -1}
                          />
                          <Input
                            $editing={Boolean(orgBusinessProfile?.canEdit && orgBusinessEditMode)}
                            value={orgBusinessForm.website}
                            onChange={(event) =>
                              setOrgBusinessForm((prev) => ({
                                ...prev,
                                website: event.target.value,
                              }))
                            }
                            readOnly={!orgBusinessEditMode || !orgBusinessProfile?.canEdit}
                            disabled={!orgBusinessProfile?.canEdit}
                            placeholder="Website"
                            tabIndex={orgBusinessEditMode && orgBusinessProfile?.canEdit ? 0 : -1}
                          />
                          <Input
                            $editing={Boolean(orgBusinessProfile?.canEdit && orgBusinessEditMode)}
                            value={orgBusinessForm.address}
                            onChange={(event) =>
                              setOrgBusinessForm((prev) => ({
                                ...prev,
                                address: event.target.value,
                              }))
                            }
                            readOnly={!orgBusinessEditMode || !orgBusinessProfile?.canEdit}
                            disabled={!orgBusinessProfile?.canEdit}
                            placeholder="Business address"
                            tabIndex={orgBusinessEditMode && orgBusinessProfile?.canEdit ? 0 : -1}
                          />
                        </Grid>
                      </div>
                    </OrgProfileRow>

                    {orgBusinessProfile?.canEdit && orgBusinessDirty ? (
                      <OrgProfileFooter>
                        <Button
                          type="button"
                          onClick={() => {
                            void saveOrganizationProfile();
                          }}
                          disabled={orgBusinessSaving}
                        >
                          {orgBusinessSaving
                            ? hasOrganizationProfile
                              ? 'Saving...'
                              : 'Creating...'
                            : hasOrganizationProfile
                              ? 'Save profile'
                              : 'Create organization'}
                        </Button>
                      </OrgProfileFooter>
                    ) : !orgBusinessProfile?.canEdit ? (
                      <OrgProfileFooter>
                        <OrgProfileIntro style={{ margin: 0 }}>
                          Only the primary account holder can edit organization profile fields.
                        </OrgProfileIntro>
                      </OrgProfileFooter>
                    ) : null}
                  </>
                )}
              </CardContent>
            </ContentCard>
          ) : null}

          <SettingsPanelRow>
            <SettingsGreenPanel as={motion.section} variants={fadeInUp} initial="hidden" animate="visible">
              <SettingsPanelTitle>Discord</SettingsPanelTitle>
              <SettingsPanelBody>
                Connect your Discord account here so onboarding, community access, and private organization rooms all use the same verified identity.
              </SettingsPanelBody>
              <SettingsField as="div">
                <SettingsFieldLabel>Account status</SettingsFieldLabel>
                <SettingsInlineRow>
                  <SettingsInlineValue>{discordAccountValue}</SettingsInlineValue>
                  {discordStatus?.connected ? (
                    <Button
                      type="button"
                      $variant="danger"
                      onClick={() => {
                        void handleUnlinkDiscord();
                      }}
                      disabled={Boolean(discordAction)}
                    >
                      {discordAction === 'unlink' ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => {
                        void handleConnectDiscord();
                      }}
                      disabled={Boolean(discordAction) || !discordStatus?.enabled}
                    >
                      {discordAction === 'connect' ? 'Connecting...' : 'Connect'}
                    </Button>
                  )}
                </SettingsInlineRow>
              </SettingsField>
              <SecurityStatus $error={discordHelperTone === 'error'}>
                {discordHelperText}
              </SecurityStatus>
              <SettingsActionRow>
                {discordStatus?.publicInviteUrl ? (
                  <CommunityActionGroup>
                    <DiscordLottieBadge aria-hidden="true">
                      <Lottie animationData={discordAnimation} loop autoplay />
                    </DiscordLottieBadge>
                    <CommunityActionButton
                      as="a"
                      href={discordStatus.publicInviteUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open community
                    </CommunityActionButton>
                  </CommunityActionGroup>
                ) : null}
              </SettingsActionRow>
            </SettingsGreenPanel>

            <SettingsGrayPanel as={motion.section} variants={fadeInUp} initial="hidden" animate="visible">
              <SettingsPanelTitle>Account Security</SettingsPanelTitle>
              <SettingsPanelBody>
                Update your password from this page. Click{' '}
                <strong>Change password</strong>{' '}
                and enter your current password, then set and confirm your new password.
              </SettingsPanelBody>
              <SecurityActionButton
                type="button"
                onClick={() => {
                  setPasswordError(null);
                  setPasswordModalOpen(true);
                }}
              >
                Change password
              </SecurityActionButton>
              {passwordSuccess ? <SecurityStatus>{passwordSuccess}</SecurityStatus> : null}

              <MfaBlock>
                <MfaTitle>Authenticator App 2FA</MfaTitle>
                <MfaPill $enabled={mfaEnabled}>{mfaEnabled ? 'Enabled' : 'Disabled'}</MfaPill>
                <MfaBody>
                  Optional second-factor sign-in using Google Authenticator, 1Password, Authy, or similar apps.
                </MfaBody>
                {mfaEnabled ? (
                  <SecurityActionButton type="button" onClick={openMfaDisable}>
                    Disable authenticator
                  </SecurityActionButton>
                ) : (
                  <SecurityActionButton type="button" onClick={openMfaSetup}>
                    Enable authenticator
                  </SecurityActionButton>
                )}
                {mfaStatusMessage ? <SecurityStatus>{mfaStatusMessage}</SecurityStatus> : null}
              </MfaBlock>
            </SettingsGrayPanel>
          </SettingsPanelRow>
        </Content>
      </Layout>

      <PasswordModalBackdrop
        type="button"
        aria-label="Close change password modal"
        $open={passwordModalOpen}
        onClick={closePasswordModal}
      />
      <PasswordModal $open={passwordModalOpen} onClick={(event) => event.stopPropagation()}>
        <PasswordModalHeader>
          <PasswordModalTitle>Change Password</PasswordModalTitle>
          <PasswordModalClose
            type="button"
            onClick={closePasswordModal}
            aria-label="Close"
          >
            x
          </PasswordModalClose>
        </PasswordModalHeader>

        <PasswordForm onSubmit={handleChangePassword}>
          <PasswordField>
            <PasswordLabel>Current password</PasswordLabel>
            <PasswordInput
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </PasswordField>

          <PasswordField>
            <PasswordLabel>New password</PasswordLabel>
            <PasswordInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </PasswordField>

          <PasswordField>
            <PasswordLabel>Confirm new password</PasswordLabel>
            <PasswordInput
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </PasswordField>

          {passwordError ? (
            <PasswordModalMessage $error>{passwordError}</PasswordModalMessage>
          ) : null}

          <PasswordActions>
            <PasswordSecondaryButton type="button" onClick={closePasswordModal}>
              Cancel
            </PasswordSecondaryButton>
            <PasswordPrimaryButton type="submit" disabled={passwordSaving}>
              {passwordSaving ? 'Saving...' : 'Update password'}
            </PasswordPrimaryButton>
          </PasswordActions>
        </PasswordForm>
      </PasswordModal>

      <PasswordModalBackdrop
        type="button"
        aria-label="Close authenticator setup modal"
        $open={mfaSetupOpen}
        onClick={() => closeMfaSetup()}
      />
      <PasswordModal $open={mfaSetupOpen} onClick={(event) => event.stopPropagation()}>
        <PasswordModalHeader>
          <PasswordModalTitle>
            {mfaSetupStep === 'reauth' ? 'Enable Authenticator 2FA' : 'Verify authenticator code'}
          </PasswordModalTitle>
          <PasswordModalClose
            type="button"
            onClick={() => closeMfaSetup()}
            aria-label="Close"
          >
            x
          </PasswordModalClose>
        </PasswordModalHeader>

        {mfaSetupStep === 'reauth' ? (
          <PasswordForm onSubmit={handleMfaStartSetup}>
            <MfaModalHint>
              Confirm your current password, then scan a QR code with your authenticator app.
            </MfaModalHint>
            <PasswordField>
              <PasswordLabel>Current password</PasswordLabel>
              <PasswordInput
                type="password"
                value={mfaSetupPassword}
                onChange={(e) => setMfaSetupPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </PasswordField>
            {mfaSetupError ? <PasswordModalMessage $error>{mfaSetupError}</PasswordModalMessage> : null}
            <PasswordActions>
              <PasswordSecondaryButton type="button" onClick={() => closeMfaSetup()}>
                Cancel
              </PasswordSecondaryButton>
              <PasswordPrimaryButton type="submit" disabled={mfaSetupLoading}>
                {mfaSetupLoading ? 'Preparing...' : 'Continue'}
              </PasswordPrimaryButton>
            </PasswordActions>
          </PasswordForm>
        ) : (
          <PasswordForm onSubmit={handleMfaVerifyAndEnable}>
            <MfaModalHint>
              Scan the QR code (or use manual key), then enter the current 6-digit code from your app.
            </MfaModalHint>
            {mfaSetupQrUrl ? (
              <MfaQrPreview aria-label="Authenticator QR code">
                <QRCodeSVG
                  value={mfaSetupQrUrl}
                  size={156}
                  bgColor="#ffffff"
                  fgColor="#111111"
                  includeMargin={false}
                />
              </MfaQrPreview>
            ) : null}
            {mfaSetupSecret ? <MfaKey>{mfaSetupSecret.secretKey}</MfaKey> : null}
            <PasswordField>
              <PasswordLabel>Authenticator code</PasswordLabel>
              <PasswordInput
                type="text"
                inputMode="numeric"
                value={mfaSetupOtp}
                onChange={(e) => setMfaSetupOtp(e.target.value)}
                autoComplete="one-time-code"
                placeholder="6-digit code"
                required
              />
            </PasswordField>
            {mfaSetupError ? <PasswordModalMessage $error>{mfaSetupError}</PasswordModalMessage> : null}
            <PasswordActions>
              <PasswordSecondaryButton
                type="button"
                onClick={() => {
                  if (mfaSetupLoading) return;
                  setMfaSetupStep('reauth');
                  setMfaSetupOtp('');
                  setMfaSetupSecret(null);
                  setMfaSetupQrUrl('');
                  setMfaSetupError(null);
                }}
              >
                Back
              </PasswordSecondaryButton>
              <PasswordPrimaryButton type="submit" disabled={mfaSetupLoading}>
                {mfaSetupLoading ? 'Verifying...' : 'Enable 2FA'}
              </PasswordPrimaryButton>
            </PasswordActions>
          </PasswordForm>
        )}
      </PasswordModal>

      <PasswordModalBackdrop
        type="button"
        aria-label="Close authenticator disable modal"
        $open={mfaDisableOpen}
        onClick={() => closeMfaDisable()}
      />
      <PasswordModal $open={mfaDisableOpen} onClick={(event) => event.stopPropagation()}>
        <PasswordModalHeader>
          <PasswordModalTitle>Disable Authenticator 2FA</PasswordModalTitle>
          <PasswordModalClose
            type="button"
            onClick={() => closeMfaDisable()}
            aria-label="Close"
          >
            x
          </PasswordModalClose>
        </PasswordModalHeader>
        <PasswordForm onSubmit={handleMfaDisable}>
          <MfaModalHint>
            Confirm your current password to remove authenticator-app login protection for this account.
          </MfaModalHint>
          <PasswordField>
            <PasswordLabel>Current password</PasswordLabel>
            <PasswordInput
              type="password"
              value={mfaDisablePassword}
              onChange={(e) => setMfaDisablePassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </PasswordField>
          {mfaDisableError ? <PasswordModalMessage $error>{mfaDisableError}</PasswordModalMessage> : null}
          <PasswordActions>
            <PasswordSecondaryButton type="button" onClick={() => closeMfaDisable()}>
              Cancel
            </PasswordSecondaryButton>
            <PasswordPrimaryButton type="submit" disabled={mfaDisableLoading}>
              {mfaDisableLoading ? 'Saving...' : 'Disable 2FA'}
            </PasswordPrimaryButton>
          </PasswordActions>
        </PasswordForm>
      </PasswordModal>
    </Wrap>
  );
}
